import * as React from 'react';
import clsx from 'clsx';
import type { Echo } from '@/types';
import { HeaderCredits } from '../../common/HeaderCredits';
import { ClearChatButton } from './ClearChatButton';
import { AudioToggleButton } from './AudioToggleButton';
import { VideoToggleButton } from './VideoToggleButton';
import { useHeaderVideo } from '@/context/useHeaderVideo';
import { Badge } from '../../common/Badge';
import { ChatStatsBadge } from '../ChatStatsBadge';
import authElement from '@/assets/images/elements/auth_element.svg';

const MASK_GRADIENTS =
  'linear-gradient(to right, transparent, black 10%, black 90%, transparent), linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)';

const maskStyle: React.CSSProperties = {
  WebkitMaskImage: MASK_GRADIENTS,
  WebkitMaskComposite: 'source-in',
  maskImage: MASK_GRADIENTS,
  maskComposite: 'intersect',
};

/** Props for the expanded video-mode chat header layout. */
interface VideoModeLayoutProps {
  echo: Echo;
  avatarSrc: string | null;
  idleVideoSrc: string | null;
  formattedFans: string;
  isGroupChat: boolean;
  isAudioEnabled: boolean;
  onAudioToggle: () => void;
  isVideoEnabled: boolean;
  onVideoToggle: () => void;
  onClear?: (() => void) | undefined;
  onClearModalOpen: () => void;
  disabled?: boolean | undefined;
}

/** Chat header layout when video mode is active. Renders dual-video (idle + lipsync) with vignette overlay. */
export const VideoModeLayout: React.FC<VideoModeLayoutProps> = ({
  echo,
  avatarSrc,
  idleVideoSrc,
  formattedFans,
  isGroupChat,
  isAudioEnabled,
  onAudioToggle,
  isVideoEnabled,
  onVideoToggle,
  onClear,
  onClearModalOpen,
  disabled,
}) => {
  const { activeVideoUrl, clearActiveVideo } = useHeaderVideo();
  const idleRef = React.useRef<HTMLVideoElement>(null);
  const lipsyncRef = React.useRef<HTMLVideoElement>(null);
  const [lipsyncReady, setLipsyncReady] = React.useState(false);

  const isPlayingLipsync = !!activeVideoUrl && lipsyncReady;
  const hasIdleVideo = !!idleVideoSrc;

  /** When a lipsync URL is set, preload it. Show it only once canplay fires. */
  React.useEffect(() => {
    const lipsync = lipsyncRef.current;
    if (!lipsync) return;

    if (!activeVideoUrl) {
      lipsync.pause();
      lipsync.removeAttribute('src');
      lipsync.load();
      setLipsyncReady(false);
      return;
    }

    setLipsyncReady(false);
    lipsync.src = activeVideoUrl;
    lipsync.load();

    const onCanPlay = () => {
      setLipsyncReady(true);
      void lipsync.play();
    };
    lipsync.addEventListener('canplay', onCanPlay, { once: true });
    return () => lipsync.removeEventListener('canplay', onCanPlay);
  }, [activeVideoUrl]);

  /** Pause/resume idle video based on lipsync state. */
  React.useEffect(() => {
    const idle = idleRef.current;
    if (!idle) return;
    if (isPlayingLipsync) {
      idle.pause();
    } else {
      void idle.play();
    }
  }, [isPlayingLipsync]);

  const handleLipsyncEnded = React.useCallback(() => {
    clearActiveVideo();
  }, [clearActiveVideo]);

  return (
    <div className='flex w-full flex-col'>
      <div className='mt-[72px] flex w-full flex-col items-center px-[18px] pt-[16px] xl:mt-0 xl:px-0 xl:pt-0'>
        <div className='hidden w-full items-start justify-between xl:flex'>
          {/* Desktop: echo info */}
          <div className='flex shrink-0 flex-col items-start'>
            <div className='flex items-center gap-2'>
              <h2 className='font-proxima text-header-title font-bold leading-105 tracking-normal text-white'>
                {echo.name}
              </h2>
              {!isGroupChat && (
                <Badge
                  label={`${formattedFans} Fans`}
                  variant='chatHeader'
                  className='shrink-0 text-[12px]'
                />
              )}
              {!isGroupChat && (
                <ChatStatsBadge
                  echoId={echo.id}
                  variant='header'
                  className='shrink-0 [&_span]:text-[12px]'
                />
              )}
            </div>
            <span className='flex items-center gap-1 font-proxima text-14 font-bold uppercase leading-105 tracking-normal text-white'>
              D.ECHO
              <span className='inline-block size-[5px] rounded-full bg-green-500' />
              {echo.category}
            </span>
          </div>

          {/* Desktop: actions */}
          <div className='flex items-center gap-3'>
            <HeaderCredits />
            {onClear && <ClearChatButton onClick={onClearModalOpen} disabled={disabled} />}
          </div>
        </div>

        {/* Video / static fallback */}
        {hasIdleVideo ? (
          <div className='grid place-items-center [grid-template-areas:"v"]'>
            <div className='pointer-events-none flex h-0 items-center justify-center overflow-visible [grid-area:v]'>
              <img
                src={authElement}
                alt=''
                className='hidden w-[320px] animate-spin-slow-120 opacity-30 xl:w-[520px]'
              />
            </div>
            <div className='relative z-10 h-[200px] w-[230px] overflow-hidden [grid-area:v] xl:h-[328px] xl:w-[377px]'>
              <video
                ref={idleRef}
                src={idleVideoSrc ?? undefined}
                className={clsx(
                  'absolute inset-0 size-full object-cover object-top',
                  isPlayingLipsync && 'invisible'
                )}
                style={maskStyle}
                loop
                muted
                autoPlay
                playsInline
              />
              <video
                ref={lipsyncRef}
                className={clsx(
                  'absolute inset-0 size-full object-cover object-top',
                  !isPlayingLipsync && 'invisible'
                )}
                style={maskStyle}
                playsInline
                onEnded={handleLipsyncEnded}
              />
              {/* Vignette: masks video edge artifacts */}
              <div className='pointer-events-none absolute inset-0'>
                <div className='absolute inset-x-0 top-0 h-[20px] bg-gradient-to-b from-brand-840 to-transparent xl:h-[30px]' />
                <div className='absolute inset-y-0 left-0 w-[20px] bg-gradient-to-r from-brand-840 to-transparent xl:w-[30px]' />
                <div className='absolute inset-y-0 right-0 w-[20px] bg-gradient-to-l from-brand-840 to-transparent xl:w-[30px]' />
              </div>
            </div>
          </div>
        ) : (
          <div className='z-10 flex h-[200px] w-[230px] items-center justify-center xl:h-[328px] xl:w-[377px]'>
            <img
              src={avatarSrc ?? undefined}
              alt={echo.name}
              className='size-full object-contain'
            />
          </div>
        )}
      </div>

      {/* Bottom bar */}
      <div className='relative mt-[-20px] xl:mt-[-33px]'>
        <div className='absolute bottom-full left-1/2 z-10 h-[20px] w-screen bg-gradient-to-t from-brand-870 to-transparent [transform:translateX(-50%)] xl:h-[33px]' />
        <div className='absolute inset-y-0 left-1/2 z-10 w-screen bg-brand-870 [transform:translateX(-50%)]' />
        <div className='relative z-20 flex flex-col items-center gap-1 py-[6px]'>
          {/* Mobile: echo info */}
          <div className='flex w-fit flex-col items-start xl:hidden'>
            <div className='flex items-center gap-2'>
              <h2 className='font-proxima text-16 font-bold leading-105 tracking-normal text-white'>
                {echo.name}
              </h2>
              {!isGroupChat && (
                <Badge
                  label={`${formattedFans} Fans`}
                  variant='chatHeader'
                  className='shrink-0 text-[10px]'
                />
              )}
              {!isGroupChat && (
                <ChatStatsBadge
                  echoId={echo.id}
                  variant='header'
                  className='shrink-0 [&_span]:text-[10px]'
                />
              )}
            </div>
            <span className='flex items-center gap-1 font-proxima text-[11px] font-bold uppercase leading-105 tracking-normal text-white'>
              D.ECHO
              <span className='inline-block size-[5px] rounded-full bg-green-500' />
              {echo.category}
            </span>
          </div>
          <div className='flex items-center justify-center gap-2'>
            <AudioToggleButton isEnabled={isAudioEnabled} onClick={onAudioToggle} />
            <VideoToggleButton isEnabled={isVideoEnabled} onClick={onVideoToggle} />
          </div>
        </div>
      </div>
    </div>
  );
};
